import { Check } from "lucide-react";

interface InvestmentDetailsCardProps {
  minChequeSize?: number;
  maxChequeSize?: number;
  currency?: string;
  prize?: string;
}

function formatChequeSize(value: number, currency?: string) {
  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: currency || "USD",
    maximumFractionDigits: 0,
  }).format(value);
}

function looksLikeHtml(value: string): boolean {
  return /<[a-z][\s\S]*>/i.test(value);
}

function stripHtml(value: string): string {
  return value.replace(/<[^>]*>/g, "").replace(/&nbsp;/g, " ").trim();
}

function parseResources(prize: string): string[] {
  // Split by newlines or bullet points
  return prize
    .split(/[\n\r]+|(?:^|\s)[-•]\s*/)
    .map((item) => item.replace(/[^\S\x20]/g, " ").replace(/\s+/g, " ").trim())
    .filter((item) => item.length > 0);
}

export function InvestmentDetailsCard({
  minChequeSize,
  maxChequeSize,
  currency,
  prize,
}: InvestmentDetailsCardProps) {
  const hasCapital = Boolean(
    (minChequeSize && minChequeSize > 0) ||
    (maxChequeSize && maxChequeSize > 0)
  );

  // Resources may be saved as rich-text HTML (from the workspace settings
  // editor) or as legacy plain text split by newlines / bullet chars. Render
  // HTML verbatim inside a prose container; fall back to the plain-text
  // parser for older workspaces whose `prize` was captured as a textarea.
  const isHtml = prize ? looksLikeHtml(prize) : false;
  const resources = prize && !isHtml
    ? parseResources(prize).filter(
      (item) =>
        item.length > 2 &&
        !/^\d[\d,.\s]*$/.test(item) &&
        !/^[$€£¥]\s*\d/.test(item) &&
        item !== "0" &&
        isNaN(Number(item))
    )
    : [];
  const hasResources = isHtml
    ? stripHtml(prize ?? "").length > 0
    : resources.length > 0;

  // Only show card if there's meaningful capital or resources
  if (!hasCapital && !hasResources) return null;

  const getCapitalDisplay = () => {
    if (minChequeSize && minChequeSize > 0 && maxChequeSize && maxChequeSize > 0) {
      return `${formatChequeSize(minChequeSize, currency)} - ${formatChequeSize(maxChequeSize, currency)}`;
    }
    if (minChequeSize && minChequeSize > 0) {
      return `${formatChequeSize(minChequeSize, currency)}+`;
    }
    if (maxChequeSize && maxChequeSize > 0) {
      return `Up to ${formatChequeSize(maxChequeSize, currency)}`;
    }
    return null;
  };

  return (
    <div className="bg-white rounded-lg border border-gray-200 p-6">
      <h3 className="text-lg font-semibold text-black mb-4">
        Investment Details
      </h3>
      <div className="space-y-6">
        {hasCapital && (
          <div>
            <p className="text-xs uppercase tracking-wide text-gray-500 mb-2">
              Capital
            </p>
            <p className="text-black font-semibold">{getCapitalDisplay()}</p>
          </div>
        )}

        {hasResources && (
          <div>
            <p className="text-xs uppercase tracking-wide text-gray-500 mb-3">
              Resources
            </p>
            {isHtml ? (
              <div
                className="prose prose-sm max-w-none text-gray-700 text-sm leading-relaxed"
                dangerouslySetInnerHTML={{ __html: prize ?? "" }}
              />
            ) : resources.length === 1 ? (
              <p className="text-gray-700 text-sm">{resources[0]}</p>
            ) : (
              <ul className="space-y-2">
                {resources.map((resource, index) => (
                  <li key={index} className="flex items-start gap-2">
                    <Check className="w-4 h-4 text-gray-400 mt-0.5 flex-shrink-0" />
                    <span className="text-gray-700 text-sm">{resource}</span>
                  </li>
                ))}
              </ul>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
